// src/pages/Chargebacks.js
import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import {
  isChargeback,
  daysActive,
  getChargebackScope,
  repCommissionOnOrder,
  normalizeDate,
  CHARGEBACK_WINDOW_DAYS,
} from '../data/chargebacks';

const TEAL = '#00d4a0';
const RED  = '#ff6b6b';
const AMBER = '#f5a623';

function weekKeyOf(dateStr) {
  const d = new Date(dateStr);
  const ws = startOfWeek(d, { weekStartsOn: 1 });
  return format(ws, 'yyyy-MM-dd');
}

function weekRangeLabel(weekKey) {
  const ws = new Date(weekKey);
  // Adjust to local date (avoid TZ shift)
  const local = new Date(ws.getUTCFullYear(), ws.getUTCMonth(), ws.getUTCDate());
  const we = endOfWeek(local, { weekStartsOn: 1 });
  return `${format(local, 'MMM d')} – ${format(we, 'MMM d, yyyy')}`;
}

export default function Chargebacks() {
  const { currentUser, userProfile } = useAuth();
  const [orders, setOrders] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repFilter, setRepFilter] = useState('all');
  const [expandedWeek, setExpandedWeek] = useState(null);

  useEffect(() => {
    async function load() {
      const [ordSnap, repSnap] = await Promise.all([
        getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'users')),
      ]);
      setOrders(ordSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, []);

  const scope = useMemo(
    () => getChargebackScope(currentUser?.uid, userProfile, reps),
    [currentUser, userProfile, reps]
  );

  // All chargeback orders visible to this user
  const visibleChargebacks = useMemo(() => {
    return orders
      .filter(isChargeback)
      .filter(o => {
        if (scope.visibleRepKeys === null) return true;
        // Order is visible if any commission earner is in the downline
        return (o.commissions || []).some(c => scope.visibleRepKeys.has(c.repId));
      });
  }, [orders, scope]);

  // Flatten per (rep, order) so each row represents one rep's share of one chargeback
  const rows = useMemo(() => {
    const out = [];
    visibleChargebacks.forEach(o => {
      (o.commissions || []).forEach(c => {
        if (scope.visibleRepKeys !== null && !scope.visibleRepKeys.has(c.repId)) return;
        if (repFilter !== 'all' && c.repId !== repFilter) return;
        out.push({
          orderId: o.id,
          repId: c.repId,
          repName: c.repName || 'Unknown',
          role: c.role || '',
          customerName: o.customerName || '—',
          productName: o.productName || o.productId || '—',
          activationDate: String(o.date || '').slice(0, 10),
          disconnectDate: String(o.disconnectDate || '').slice(0, 10),
          days: daysActive(o),
          amount: c.amount || 0,
          weekKey: weekKeyOf(o.disconnectDate),
        });
      });
    });
    return out;
  }, [visibleChargebacks, scope, repFilter]);

  // Group by pay week (Mon–Sun) keyed by disconnect date's week
  const byWeek = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if (!map[r.weekKey]) map[r.weekKey] = { weekKey: r.weekKey, rows: [], total: 0, count: 0 };
      map[r.weekKey].rows.push(r);
      map[r.weekKey].total += r.amount;
      map[r.weekKey].count += 1;
    });
    return Object.values(map).sort((a, b) => b.weekKey.localeCompare(a.weekKey));
  }, [rows]);

  const grandTotal = rows.reduce((a, r) => a + r.amount, 0);
  const grandCount = rows.length;

  // Diagnostics — visible in empty state so we can see why rows are filtered out
  const diag = useMemo(() => {
    // Sentinel check using same normalization as chargebacks.js helper.
    const BAD = new Set(['1900-01-01', '1900-01-02', '1901-01-01', '2000-01-01', '2000-01-02', '2001-01-01']);
    const isReal = v => {
      const s = normalizeDate(v);
      if (!s) return false;
      if (BAD.has(s)) return false;
      if (s < '2020-01-01') return false;
      return true;
    };
    const daysBetween = (a, b) => {
      const sa = normalizeDate(a);
      const sb = normalizeDate(b);
      if (!sa || !sb) return null;
      return (new Date(sb).getTime() - new Date(sa).getTime()) / 86400000;
    };
    const total = orders.length;
    const withDisc = orders.filter(o => o.disconnectDate);
    const realDisc = withDisc.filter(o => isReal(o.disconnectDate));
    const realDiscRealAct = realDisc.filter(o => isReal(o.date));
    const within90 = realDiscRealAct.filter(o => {
      const d = daysBetween(o.date, o.disconnectDate);
      return d !== null && d > 0 && d <= 90;
    });
    const over90 = realDiscRealAct.filter(o => {
      const d = daysBetween(o.date, o.disconnectDate);
      return d !== null && d > 90;
    });
    // Show samples from realDisc so we see actual non-sentinel dates, not 1/1/01 noise.
    const sampleSrc = realDisc.length ? realDisc : withDisc;
    const sample = sampleSrc.slice(0, 5).map(o => ({
      cust: o.customerName,
      date: normalizeDate(o.date) || String(o.date || 'null').slice(0, 10),
      disc: normalizeDate(o.disconnectDate) || String(o.disconnectDate || 'null').slice(0, 10),
      hasComms: (o.commissions || []).length > 0,
    }));
    return {
      total,
      withDisc: withDisc.length,
      realDisc: realDisc.length,
      realBoth: realDiscRealAct.length,
      within90: within90.length,
      over90: over90.length,
      withCommissions: within90.filter(o => (o.commissions || []).length > 0).length,
      sample,
    };
  }, [orders]);

  // Rep filter options — restricted to scope
  const repOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach(r => { if (!seen.has(r.repId)) seen.set(r.repId, r.repName); });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  function exportCSV() {
    const lines = ['Pay Week,Rep,Role,Customer,Product,Activation Date,Disconnect Date,Days Active,Chargeback Amount'];
    byWeek.forEach(w => {
      w.rows.forEach(r => {
        lines.push([
          weekRangeLabel(w.weekKey),
          `"${r.repName}"`,
          r.role,
          `"${r.customerName}"`,
          `"${r.productName}"`,
          r.activationDate,
          r.disconnectDate,
          r.days ?? '',
          r.amount,
        ].join(','));
      });
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `chargebacks_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  }

  if (loading) return <div style={{ padding: 60, color: '#555' }}>Loading chargebacks...</div>;

  const scopeLabel =
    scope.scope === 'admin'   ? 'All reps' :
    scope.scope === 'manager' ? `Your downline (${scope.visibleRepKeys.size} reps)` :
                                'Your chargebacks';

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Chargebacks</h1>
          <p style={S.sub}>
            Disconnects within {CHARGEBACK_WINDOW_DAYS} days of activation · {scopeLabel}
          </p>
        </div>
        <div style={S.controls}>
          {repOptions.length > 1 && (
            <select style={S.select} value={repFilter} onChange={e => setRepFilter(e.target.value)}>
              <option value="all">All reps in scope</option>
              {repOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
          <button style={S.exportBtn} onClick={exportCSV}>Export CSV</button>
        </div>
      </div>

      <div style={S.metrics}>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Total chargebacks</div>
          <div style={{ ...S.metricValue, color: RED }}>{grandCount.toLocaleString()}</div>
        </div>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Dollar impact</div>
          <div style={{ ...S.metricValue, color: RED }}>${Math.round(grandTotal).toLocaleString()}</div>
        </div>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Avg per chargeback</div>
          <div style={{ ...S.metricValue, color: AMBER }}>
            ${grandCount ? Math.round(grandTotal / grandCount).toLocaleString() : 0}
          </div>
        </div>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Pay weeks affected</div>
          <div style={{ ...S.metricValue, color: '#fff' }}>{byWeek.length}</div>
        </div>
      </div>

      {byWeek.length === 0 ? (
        <div style={S.empty}>
          <div style={{ fontSize: 14, color: '#888', marginBottom: 14 }}>No chargebacks match the 90-day window.</div>
          <div style={{ fontSize: 11, color: '#555', textAlign: 'left', maxWidth: 520, margin: '0 auto', lineHeight: 1.8 }}>
            <div>Total orders in DB: <b style={{ color: '#aaa' }}>{diag.total.toLocaleString()}</b></div>
            <div>With a disconnect date field set: <b style={{ color: '#aaa' }}>{diag.withDisc.toLocaleString()}</b></div>
            <div>...disconnect date is REAL (not sentinel): <b style={{ color: '#aaa' }}>{diag.realDisc.toLocaleString()}</b></div>
            <div>...AND activation date is REAL: <b style={{ color: '#aaa' }}>{diag.realBoth.toLocaleString()}</b></div>
            <div>...AND disconnect within 90 days: <b style={{ color: '#aaa' }}>{diag.within90.toLocaleString()}</b></div>
            <div>...AND has commissions: <b style={{ color: '#aaa' }}>{diag.withCommissions.toLocaleString()}</b></div>
            <div style={{ marginTop: 8, color: '#777' }}>Side note: {diag.over90.toLocaleString()} disconnect &gt; 90 days after activation.</div>
            <div style={{ marginTop: 14, color: '#666' }}>Sample of 5 orders with disconnect dates:</div>
            <table style={{ width: '100%', marginTop: 6, fontSize: 10 }}>
              <thead><tr><th style={{ textAlign: 'left', color: '#444' }}>Customer</th><th style={{ textAlign: 'left', color: '#444' }}>Activation</th><th style={{ textAlign: 'left', color: '#444' }}>Disconnect</th><th style={{ textAlign: 'left', color: '#444' }}>Commissions?</th></tr></thead>
              <tbody>
                {diag.sample.map((s, i) => (
                  <tr key={i}>
                    <td style={{ color: '#888' }}>{s.cust}</td>
                    <td style={{ color: '#888' }}>{s.date}</td>
                    <td style={{ color: '#888' }}>{s.disc}</td>
                    <td style={{ color: s.hasComms ? '#00d4a0' : '#ff6b6b' }}>{s.hasComms ? 'yes' : 'no'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        byWeek.map(w => {
          const isOpen = expandedWeek === w.weekKey;
          return (
            <div key={w.weekKey} style={S.weekCard}>
              <div style={S.weekHeader} onClick={() => setExpandedWeek(isOpen ? null : w.weekKey)}>
                <div>
                  <div style={S.weekLabel}>{weekRangeLabel(w.weekKey)}</div>
                  <div style={S.weekSub}>{w.count} chargeback{w.count !== 1 ? 's' : ''}</div>
                </div>
                <div style={S.weekRight}>
                  <span style={S.weekAmt}>−${Math.round(w.total).toLocaleString()}</span>
                  <span style={S.chev}>{isOpen ? '▾' : '▸'}</span>
                </div>
              </div>
              {isOpen && (
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        {scope.scope !== 'rep' && <th style={S.th}>Rep</th>}
                        <th style={S.th}>Customer</th>
                        <th style={S.th}>Product</th>
                        <th style={S.th}>Activated</th>
                        <th style={S.th}>Disconnected</th>
                        <th style={S.th}>Days active</th>
                        <th style={{ ...S.th, textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {w.rows.map((r, i) => (
                        <tr key={`${r.orderId}-${r.repId}-${i}`}>
                          {scope.scope !== 'rep' && (
                            <td style={S.td}>
                              <div style={{ fontWeight: 500 }}>{r.repName}</div>
                              <div style={{ fontSize: 10, color: '#555' }}>{r.role}</div>
                            </td>
                          )}
                          <td style={S.td}>{r.customerName}</td>
                          <td style={{ ...S.td, color: '#888' }}>{r.productName}</td>
                          <td style={{ ...S.td, color: '#888' }}>{r.activationDate}</td>
                          <td style={{ ...S.td, color: RED }}>{r.disconnectDate}</td>
                          <td style={{ ...S.td, color: r.days <= 30 ? RED : r.days <= 60 ? AMBER : '#888' }}>
                            {r.days ?? '—'}d
                          </td>
                          <td style={{ ...S.td, textAlign: 'right', color: RED, fontWeight: 600 }}>
                            −${Math.round(r.amount).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 12, color: '#555', margin: '4px 0 0' },
  controls: { display: 'flex', gap: 8, alignItems: 'center' },
  select: { background: '#13131a', border: '1px solid #2a2a3a', borderRadius: 8, padding: '7px 12px', color: '#ccc', fontSize: 12, outline: 'none' },
  exportBtn: { background: TEAL, border: 'none', borderRadius: 8, padding: '7px 14px', color: '#000', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 },
  metricCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, padding: '16px 18px' },
  metricLabel: { fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  metricValue: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.5px' },
  weekCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10, marginBottom: 10, overflow: 'hidden' },
  weekHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', cursor: 'pointer' },
  weekLabel: { fontSize: 14, fontWeight: 600, color: '#fff' },
  weekSub: { fontSize: 11, color: '#555', marginTop: 2 },
  weekRight: { display: 'flex', alignItems: 'center', gap: 12 },
  weekAmt: { fontSize: 14, fontWeight: 600, color: RED },
  chev: { fontSize: 14, color: '#666', width: 14, textAlign: 'center' },
  tableWrap: { borderTop: '1px solid #1e1e2a', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 700 },
  th: { textAlign: 'left', padding: '8px 14px', fontSize: 10, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a', background: '#0f0f15' },
  td: { padding: '10px 14px', fontSize: 12, color: '#ccc', borderBottom: '1px solid #111' },
  empty: { color: '#444', fontSize: 13, textAlign: 'center', padding: 60, background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 10 },
};
